import React, { createContext, FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import qs from 'qs';

// core
import { INITIAL_PAGINATION } from 'core/constants';

// redux
import { resetError } from 'redux/error-service/action';
import { resetOpportunities } from 'redux/opportunities-service/action';
import {
	selectorGetChallengeOpportunitiesCount,
	selectorGetManualOpportunitiesCount,
	selectorGetPastOpportunitiesCount,
	selectorGetTotalOpportunitiesCount,
	selectorGetUpcomingOpportunitiesCount,
} from 'redux/opportunities-service/selector';

// type
import {
	AllOpposQuery,
	ChallengeOpposQuery,
	ManualOpposQuery,
	PastOpposQuery,
	UpcomingOpposQuery,
} from 'core/types/OPPORTUNITIES_QUERIES';

// components
import OpportunitiesTable, { OpportunitiesRequestBody } from 'components/Organization/Opportunities/Table';
import TabNavItem from '../TabNav/TabNavItem';
import TabNav from '../TabNav';

// style
import styles from './OppListContent.module.scss';

export enum OPPORTUNITIES_TABS {
	// eslint-disable-next-line no-unused-vars
	ALL = 'ALL',
	// eslint-disable-next-line no-unused-vars
	CHALLENGE = 'CHALLENGE',
	// eslint-disable-next-line no-unused-vars
	UPCOMING = 'UPCOMING',
	// eslint-disable-next-line no-unused-vars
	MANUAL = 'MANUAL',
	// eslint-disable-next-line no-unused-vars
	PAST = 'PAST',
	// eslint-disable-next-line no-unused-vars
	PLACEHOLDER = 'PLACEHOLDER',
}

const AllTabQueryPreset = {
	...AllOpposQuery,
	oppoFilterType: OPPORTUNITIES_TABS.ALL,
};
const ChallengeTabQueryPreset = {
	...ChallengeOpposQuery,
	oppoFilterType: OPPORTUNITIES_TABS.CHALLENGE,
};
const PastTabQueryPreset = {
	...PastOpposQuery,
	oppoFilterType: OPPORTUNITIES_TABS.PAST,
};
const UpcomingTabQueryPreset = {
	...UpcomingOpposQuery,
	oppoFilterType: OPPORTUNITIES_TABS.UPCOMING,
};
const ManualTabQueryPreset = {
	...ManualOpposQuery,
	oppoFilterType: OPPORTUNITIES_TABS.MANUAL,
};

const QueryPreset = new Map();
QueryPreset.set(OPPORTUNITIES_TABS.ALL, AllTabQueryPreset);
QueryPreset.set(OPPORTUNITIES_TABS.PAST, PastTabQueryPreset);
QueryPreset.set(OPPORTUNITIES_TABS.UPCOMING, UpcomingTabQueryPreset);
QueryPreset.set(OPPORTUNITIES_TABS.MANUAL, ManualTabQueryPreset);
QueryPreset.set(OPPORTUNITIES_TABS.CHALLENGE, ChallengeTabQueryPreset);

export const OppoContext = createContext({});

const parseQueryString = (search: string): OpportunitiesRequestBody => {
	// if (searchParams.showVacancies) searchParams.showVacancies = !!searchParams.showVacancies;
	return qs.parse(search, { ignoreQueryPrefix: true });
};

const OppListContent: FC = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const history = useHistory();

	const [pagination, setPagination] = useState(INITIAL_PAGINATION);
	const initTab = () => {
		if (location.search.includes('past')) return OPPORTUNITIES_TABS.PAST;
		if (location.search.includes('upcoming')) return OPPORTUNITIES_TABS.UPCOMING;
		if (location.search.includes('manual')) return OPPORTUNITIES_TABS.MANUAL;
		if (location.search.includes('placeholder')) return OPPORTUNITIES_TABS.PLACEHOLDER;
		return OPPORTUNITIES_TABS.ALL;
	};
	const [activeTab, setActiveTab] = useState<OPPORTUNITIES_TABS>(initTab);
	const [requestBody, setRequestBody] = useState<OpportunitiesRequestBody | undefined>(QueryPreset.get(activeTab));

	const opportunitiesManualCount = useSelector(selectorGetManualOpportunitiesCount);
	const opportunitiesPastCount = useSelector(selectorGetPastOpportunitiesCount);
	const opportunitiesChallengeCount = useSelector(selectorGetChallengeOpportunitiesCount);

	const opportunitiesTotalCount = useSelector(selectorGetTotalOpportunitiesCount);
	const opportunitiesUpcomingCount = useSelector(selectorGetUpcomingOpportunitiesCount);

	const [isClearSearch, setIsClearSearch] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(true);

	const allTabClickHandler = () => {
		setActiveTab(OPPORTUNITIES_TABS.ALL);
	};
	const challengeTabClickHandler = () => {
		setActiveTab(OPPORTUNITIES_TABS.CHALLENGE);
	};
	const pastTabClickHandler = () => {
		setActiveTab(OPPORTUNITIES_TABS.PAST);
	};
	const upcomingTabClickHandler = () => {
		setActiveTab(OPPORTUNITIES_TABS.UPCOMING);
	};
	const manualTabClickHandler = () => {
		setActiveTab(OPPORTUNITIES_TABS.MANUAL);
	};

	const generalTabClickHandler = async () => {
		setIsClearSearch(true);
		setPagination(INITIAL_PAGINATION);
		dispatch(resetError());
		dispatch(resetOpportunities());
		history.push({ search: activeTab !== OPPORTUNITIES_TABS.ALL ? `?${activeTab.toLowerCase()}` : '' });
		if (!isFirstLoad) setRequestBody(QueryPreset.get(activeTab));
		if (isFirstLoad) setIsFirstLoad(false);
	};

	useEffect(() => {
		generalTabClickHandler().then();
	}, [activeTab]);

	useEffect(() => {
		return () => {
			dispatch(resetOpportunities());
			dispatch(resetError());
			generalTabClickHandler().then();
			history.push({ search: '' });
		};
	}, []);

	useEffect(() => {
		dispatch(resetOpportunities());
		setPagination(INITIAL_PAGINATION);
		setRequestBody({ ...QueryPreset.get(activeTab), ...parseQueryString(location.search) });
	}, [location.search]);

	return (
		<div className={styles.container}>
			<TabNav>
				<TabNavItem
					counter={opportunitiesTotalCount || 0}
					title="All"
					activeTab={activeTab === OPPORTUNITIES_TABS.ALL}
					clickHandler={allTabClickHandler}
				/>
				<TabNavItem
					counter={opportunitiesManualCount || 0}
					activeTab={activeTab === OPPORTUNITIES_TABS.MANUAL}
					title="Pending Approval"
					clickHandler={manualTabClickHandler}
				/>
				<TabNavItem
					counter={opportunitiesChallengeCount || 0}
					activeTab={activeTab === OPPORTUNITIES_TABS.CHALLENGE}
					title="Challenge"
					clickHandler={challengeTabClickHandler}
				/>
				<TabNavItem
					counter={opportunitiesPastCount || 0}
					activeTab={activeTab === OPPORTUNITIES_TABS.PAST}
					title="Past"
					clickHandler={pastTabClickHandler}
				/>
				<TabNavItem
					counter={opportunitiesUpcomingCount || 0}
					activeTab={activeTab === OPPORTUNITIES_TABS.UPCOMING}
					title="Upcoming"
					clickHandler={upcomingTabClickHandler}
				/>
			</TabNav>
			<OppoContext.Provider value={{ isClearSearch, setIsClearSearch }}>
				<OpportunitiesTable
					requestBody={requestBody}
					pagination={pagination}
					setPagination={setPagination}
					sort={requestBody?.sort}
					setRequestBody={setRequestBody}
					currentTab={activeTab}
					// changeVisibilityFilters={() => console.log('oppoList')}
				/>
			</OppoContext.Provider>
		</div>
	);
};

export default OppListContent;
